import moment from 'moment'

import {LoadSpacingScenario} from '../../fields/loadSpacing/types'
import {formatTimeToHHMMSS} from '../../utils/timeUtils'

import {SingleSlotParams, SlotFormData} from './types'
import {validateSlotFormData} from './validateSlotFormData'

/**
 * Options for building slot request
 */
export interface BuildSlotRequestOptions {
  /** Organization unit ID */
  orgUnitId: string
  /** Site number for delivery */
  siteNumber?: string
  /** Optional: existing order ID (for Order Management) */
  orderId?: string
  /** Scenario defining which spacing fields are required (optional) */
  loadSpacingScenario?: LoadSpacingScenario
  /** Whether to normalize date to 12:00 PM UTC (true for availability, false for validate) */
  normalizeDate?: boolean
  /** Fallback contract number when form contract field is absent (e.g. empty options list) */
  contractNumber?: string
  /** Fallback contract item position number when form contract field is absent */
  contractItemPositionNumber?: string
  /** Optional resolver for replacing the form dateTime before request params are built */
  resolveDateTime?: (currentDateTime: string) => string
}

/**
 * Result of building slot request
 */
export type BuildSlotRequestResult =
  | {
      isValid: true
      params: SingleSlotParams
    }
  | {
      isValid: false
      params: null
      missingFields: string[]
    }

/**
 * Normalizes date to 12:00 PM UTC
 * Backend requires normalized dates for slot endpoints
 */
const normalizeToTwelvePmUtc = (date: string | moment.Moment): string => {
  return moment(date).utc().hours(12).minutes(0).seconds(0).milliseconds(0).toISOString()
}

/**
 * Builds SingleSlotParams from form data
 */
const buildSlotParams = (
  formData: SlotFormData,
  options: BuildSlotRequestOptions,
  dateTime: string
): SingleSlotParams => {
  const {orgUnitId, siteNumber, orderId, normalizeDate = true} = options

  return {
    orgUnitId,
    siteNumber: siteNumber ?? '',
    orderId,
    shippingDate: normalizeDate ? normalizeToTwelvePmUtc(dateTime) : dateTime,
    contractNumber: (formData.contract?.contractNumber ?? options.contractNumber)!,
    contractItemNumber: (formData.contract?.contractItemPositionNumber ??
      options.contractItemPositionNumber)!,
    quantity:
      formData.quantity.type === 'volume'
        ? formData.quantity.volume
        : formData.quantity.minQuantity,
    truckType: formData.truckType,
    unloadingMethod:
      formData.unloadingMethod?.pump?.pumpNumber || formData.unloadingMethod?.unloadingMethodCode,
    plusLoadExpected: formData.plusLoadExpected || undefined,
    ...(formData.spacingOrFlowRate && 'loadSpace' in formData.spacingOrFlowRate
      ? {loadSpacing: formatTimeToHHMMSS(formData.spacingOrFlowRate.loadSpace)}
      : {}),
    ...(formData.spacingOrFlowRate && 'flowRate' in formData.spacingOrFlowRate
      ? {flowRate: String(formData.spacingOrFlowRate.flowRate)}
      : {}),
    ...(formData.timeOnJobSite
      ? {totalTimeOnJobSite: formatTimeToHHMMSS(formData.timeOnJobSite.time)}
      : {})
  }
}

/**
 * Builds a single slot request from form data.
 * Used by both availability and validate endpoints.
 *
 * @param formData - Form data containing all slot-related fields
 * @param options - Configuration with either dateIndex (from form) or dateTime (custom)
 * @returns Result with validation status and slot params
 *
 * @example
 * ```tsx
 * // Using date at specific index
 * const result = buildSlotRequest(formData, {
 *   orgUnitId: '026',
 *   dateIndex: 0
 * })
 *
 * // Using custom date
 * const result = buildSlotRequest(formData, {
 *   orgUnitId: '026',
 *   dateTime: '2024-12-05T12:00:00Z'
 * })
 * ```
 */
export const buildSlotRequest = (
  formData: SlotFormData,
  options: BuildSlotRequestOptions & ({dateIndex: number} | {dateTime: string})
): BuildSlotRequestResult => {
  const dateTime = 'dateTime' in options ? options.dateTime : formData.dateTime[options.dateIndex]
  const resolvedDateTime = options.resolveDateTime?.(dateTime) ?? dateTime

  const missingFields = validateSlotFormData(formData, {
    loadSpacingScenario: options.loadSpacingScenario,
    dateTime: resolvedDateTime,
    contractNumber: options.contractNumber,
    contractItemPositionNumber: options.contractItemPositionNumber,
    orgUnitId: options.orgUnitId
  })

  if (missingFields.length > 0) {
    return {
      isValid: false,
      params: null,
      missingFields
    }
  }

  return {
    isValid: true,
    params: buildSlotParams(formData, options, resolvedDateTime)
  }
}
